import React, { useState, useEffect, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GET_OFFER_CODE_DETAILS, VALIDATE_OFFER_CODE, REDEEM_OFFER_CODE } from '@shared/graphql/mutations/offer-codes';
import { PENDING_PROMO_CODE_KEY } from './PromoCodeLauncher';

interface OfferDetails {
  code: string;
  offerName: string;
  offerDescription?: string;
  packageName: string;
  packageCode: string;
  offerType: 'FREE_GRANT' | 'STRIPE_DISCOUNT';
  accessDurationDays?: number;
  billingCycle?: string;
  expiresAt?: string;
  isValid: boolean;
  error?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialCode?: string;
}

const PromoCodeModal: React.FC<Props> = ({ isOpen, onClose, initialCode }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [inputCode, setInputCode] = useState(initialCode || '');
  const [offerDetails, setOfferDetails] = useState<OfferDetails | null>(null);
  const [step, setStep] = useState<'enter' | 'preview' | 'success'>('enter');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingAutoCheck = useRef<string | null>(null);

  const [fetchDetails, { loading: detailsLoading }] = useLazyQuery(GET_OFFER_CODE_DETAILS);
  const [validateCode, { loading: validating }] = useLazyQuery(VALIDATE_OFFER_CODE);
  const [redeemCode, { loading: redeeming }] = useMutation(REDEEM_OFFER_CODE);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setInputCode(initialCode || '');
      setOfferDetails(null);
      setError(null);
      setStep('enter');
      pendingAutoCheck.current = initialCode || null;
      if (!initialCode) setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, initialCode]);

  // Auto-check once auth has resolved — avoids stale isAuthenticated closure
  useEffect(() => {
    if (isOpen && pendingAutoCheck.current) {
      const code = pendingAutoCheck.current;
      pendingAutoCheck.current = null;
      handleCheckCode(code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAuthenticated]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  async function handleCheckCode(codeOverride?: string) {
    const code = (codeOverride || inputCode).trim().toUpperCase();
    if (!code) return;
    setError(null);
    setOfferDetails(null);
    try {
      if (isAuthenticated) {
        const { data } = await validateCode({ variables: { code } });
        const result = data?.validateOfferCode;
        if (result) {
          setOfferDetails(result);
          if (result.isValid) setStep('preview');
          else setError(result.error || 'Code is not valid');
        }
      } else {
        const { data } = await fetchDetails({ variables: { code } });
        const result = data?.getOfferCodeDetails;
        if (result) {
          setOfferDetails(result);
          if (result.isValid) setStep('preview');
          else setError(result.error || 'Code is not valid');
        }
      }
    } catch {
      setError('Unable to check code. Please try again.');
    }
  }

  async function handleRedeem() {
    if (!isAuthenticated) {
      // The code is parked rather than carried in the URL: LoginPage reads
      // router state, not ?redirect=, so the query string this used to build
      // was discarded and the code lost. PromoCodeLauncher picks it back up
      // on the home page once signed in.
      const parked = inputCode.trim().toUpperCase();
      try {
        sessionStorage.setItem(PENDING_PROMO_CODE_KEY, parked);
      } catch {
        // Blocked storage: sign-in still works, the code just needs re-entering.
      }
      navigate('/login', { state: { from: { pathname: '/' }, promoCode: parked } });
      return;
    }
    setError(null);
    try {
      const { data } = await redeemCode({ variables: { code: inputCode.trim().toUpperCase() } });
      const result = data?.redeemOfferCode;
      if (!result?.success) {
        setError(result?.error || 'Failed to redeem code');
        return;
      }
      if (result.offerType === 'STRIPE_DISCOUNT' && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setStep('success');
    } catch {
      setError('An error occurred. Please try again.');
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-deep-navy">Redeem Promo Code</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {step === 'success' ? (
            <div className="text-center py-4 space-y-3">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-deep-navy">Offer Applied!</p>
                {offerDetails && (
                  <p className="text-sm text-gray-500 mt-1">
                    {offerDetails.accessDurationDays
                      ? `You now have ${offerDetails.accessDurationDays} days of ${offerDetails.packageName} access.`
                      : `${offerDetails.packageName} has been added to your account.`}
                  </p>
                )}
              </div>
              <button
                onClick={() => { onClose(); const d = new Date(); navigate(`/${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`); }}
                className="w-full py-2.5 bg-racing-green hover:bg-racing-green/90 text-white rounded-xl font-semibold transition-colors text-sm"
              >
                Start exploring tips
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter your code</label>
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputCode}
                    onChange={e => {
                      setInputCode(e.target.value.toUpperCase());
                      setOfferDetails(null);
                      setError(null);
                      setStep('enter');
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') handleCheckCode(); }}
                    placeholder="e.g. TIQ-ABCD1234"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-racing-green/50 focus:border-racing-green transition-colors"
                  />
                  <button
                    onClick={() => handleCheckCode()}
                    disabled={!inputCode.trim() || detailsLoading || validating}
                    className="px-4 py-2 bg-racing-green hover:bg-racing-green/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    {detailsLoading || validating ? 'Checking…' : 'Check'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              {offerDetails?.isValid && step === 'preview' && (
                <div className="space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-deep-navy text-sm">{offerDetails.offerName}</p>
                        {offerDetails.offerDescription && (
                          <p className="text-gray-500 text-xs mt-0.5">{offerDetails.offerDescription}</p>
                        )}
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 border border-green-200 rounded-full px-2 py-0.5 shrink-0">Valid</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Package</span>
                        <span className="font-medium text-deep-navy">{offerDetails.packageName || 'Any package'}</span>
                      </div>
                      {offerDetails.offerType === 'FREE_GRANT' && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Access</span>
                          <span className="font-medium text-deep-navy">
                            {offerDetails.accessDurationDays ? `${offerDetails.accessDurationDays} days free` : 'Free access'}
                          </span>
                        </div>
                      )}
                      {offerDetails.offerType === 'STRIPE_DISCOUNT' && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Type</span>
                          <span className="font-medium text-deep-navy">Discounted subscription</span>
                        </div>
                      )}
                      {offerDetails.expiresAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Expires</span>
                          <span className="font-medium text-deep-navy">
                            {new Date(offerDetails.expiresAt).toLocaleDateString('en-AU')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {!isAuthenticated && (
                    <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      You&apos;ll need to log in or register to apply this offer to your account.
                    </p>
                  )}

                  <button
                    onClick={handleRedeem}
                    disabled={redeeming}
                    className="w-full py-2.5 bg-racing-green hover:bg-racing-green/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold text-sm transition-colors"
                  >
                    {redeeming
                      ? 'Applying…'
                      : !isAuthenticated
                      ? 'Log in to apply this offer'
                      : offerDetails.offerType === 'STRIPE_DISCOUNT'
                      ? 'Continue to checkout'
                      : 'Apply to my account'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoCodeModal;
